import { customMappings } from './customMappings';
import { normalizeEncName } from './utils';

type MyBufferEncoding =
	| 'ascii'
	| 'utf8'
	| 'utf16le'
	| 'ucs2'
	| 'base64'
	| 'latin1'
	| 'binary'
	| 'hex';
type MyBufferEncodingEx = MyBufferEncoding | 'cp437' | 'cp1252' | 'macintosh';

const knownMaps: ReadonlyMap<string, MyBufferEncodingEx> = new Map<string, MyBufferEncodingEx>([
	['us-ascii', 'ascii'],
	['utf-16', 'utf16le'],

	['437', 'cp437'],
	['ibm437', 'cp437'],

	['1252', 'cp1252'],
	['windows1252', 'cp1252'],

	['mac', 'macintosh'],
	['xmacroman', 'macintosh'],
	['csmacintosh', 'macintosh'],
]);

const nodeNative: ReadonlySet<BufferEncoding> = new Set<MyBufferEncoding>([
	'ascii',
	'utf8',
	'utf16le',
	'ucs2',
	'base64',
	'latin1',
	'binary',
	'hex',
]);

function isNodeNative(enc: string): enc is MyBufferEncoding {
	return nodeNative.has(enc as any);
}

/**
 * TODO: RDJ: Text perf difference of binary string input vs Buffer.from(input, 'binary')
 * @param buf
 * @param enc
 * @returns
 */
export function decodeStringNative(buf: Buffer | string, enc: keyof typeof customMappings): string {
	const charBuf = customMappings[enc];
	if (!charBuf) {
		throw new Error('Unsupported native encoding: ' + enc);
	}

	// Constructed as a utf16le buffer for efficiency
	const output = Buffer.allocUnsafe(buf.length * 2);

	if (typeof buf === 'string') {
		for (let i = 0; i < buf.length; ++i) {
			const byte = buf.charCodeAt(i);
			output[i * 2] = charBuf[byte * 2];
			output[i * 2 + 1] = charBuf[byte * 2 + 1];
		}
	} else {
		for (let i = 0; i < buf.length; ++i) {
			const byte = buf[i];
			output[i * 2] = charBuf[byte * 2];
			output[i * 2 + 1] = charBuf[byte * 2 + 1];
		}
	}

	return output.toString('utf16le');
}

export function decodeString(buf: Buffer, enc?: string): string {
	enc = normalizeEncName(enc || 'utf8');
	enc = knownMaps.get(enc) || enc;

	if (isNodeNative(enc)) {
		return buf.toString(enc);
	} else if (enc === 'utf16be') {
		const buf2 = Buffer.from(buf);
		buf2.swap16();
		return buf2.toString('utf16le');
	} else if (enc === 'cp1252' || enc === 'cp437' || enc === 'macintosh' || enc === 'pdfdoc') {
		return decodeStringNative(buf, enc);
	}

	throw new Error('Unsupported native encoding: ' + enc);
}

const replacement = 63;
export function encodeString(str: string, enc?: string): Buffer {
	enc = normalizeEncName(enc || 'utf8');
	enc = knownMaps.get(enc) || enc;

	if (isNodeNative(enc)) {
		return Buffer.from(str, enc);
	} else if (enc === 'cp1252' || enc === 'cp437' || enc === 'macintosh' || enc === 'pdfdoc') {
		// INEFFICIENT
		const charBuf = customMappings[enc];
		if (charBuf) {
			const output: number[] = [];

			for (const c of str) {
				let num = replacement;

				if (c.codePointAt(0)! < 0x10000) {
					const buf = Buffer.from(c, 'utf16le');

					for (let i = 0; i < 256; ++i) {
						if (charBuf[i * 2] === buf[0] && charBuf[i * 2 + 1] === buf[1]) {
							num = i;
							break;
						}
					}
				}

				output.push(num);
			}

			return Buffer.from(output);
		}
	}

	throw new Error('Unsupported native encoding: ' + enc);
}
